import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {User, Phone, MapPin, Building, CheckCircle, AlertCircle, Shield, Lock, Calendar, ArrowLeft} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalProfileSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasPin, setHasPin] = useState(false);
  const [showPinForm, setShowPinForm] = useState(false);
  const [pinMode, setPinMode] = useState('set');
  const [pinData, setPinData] = useState({ current_pin: '', current_password: '', new_pin: '', confirm_pin: '' });
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');
  const [useForgotPin, setUseForgotPin] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    company: '',
    date_of_birth: '',
    preferred_contact_method: 'phone',
    ssn: ''
  });

  useEffect(() => {
    loadProfile();
    // Check if this is first login
    const clientInfo = clientPortalService.getClientInfo();
    setIsFirstLogin(clientInfo?.is_first_login || clientInfo?.profile_needs_update || false);
  }, []);

  const loadProfile = async () => {
    try {
      const profile = await clientPortalService.getProfile();
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        company: profile.company || '',
        date_of_birth: profile.date_of_birth || '',
        preferred_contact_method: profile.preferred_contact_method || 'phone',
        ssn: ''
      });
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }

    // Fetch PIN status (non-blocking)
    if (!isFirstLogin) {
      clientPortalService.getPinStatus().then(data => {
        setHasPin(data.has_pin);
      }).catch(() => {});
    }
  };

  const handleCancel = () => {
    navigate('/portal/settings');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFormValid = () => {
    return formData.first_name.trim() &&
           formData.last_name.trim() &&
           formData.phone.trim() &&
           formData.address.trim() &&
           formData.city.trim() &&
           formData.state.trim() &&
           formData.zip_code.trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const submitData = { ...formData };
      if (!submitData.ssn) delete submitData.ssn;
      await clientPortalService.updateProfile(submitData);
      clientPortalService.clearProfileSetupFlag();
      setSuccess(true);

      // Navigate to next onboarding step after a brief delay
      setTimeout(() => {
        if (clientPortalService.needsPinSetup()) {
          navigate('/portal/pin-setup');
        } else if (clientPortalService.needsIntake()) {
          navigate('/portal/intake');
        } else {
          navigate('/portal/dashboard');
        }
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError('');
    setPinSuccess('');

    if (!/^\d{6}$/.test(pinData.new_pin)) {
      setPinError('PIN must be exactly 6 digits');
      return;
    }
    if (pinData.new_pin !== pinData.confirm_pin) {
      setPinError('PINs do not match');
      return;
    }

    setPinLoading(true);
    try {
      if (pinMode === 'set') {
        await clientPortalService.setPin(pinData.new_pin);
        setHasPin(true);
        clientPortalService.clearPinSetupFlag();
        setPinSuccess('PIN has been set successfully');
      } else {
        const currentPin = useForgotPin ? null : pinData.current_pin;
        const currentPassword = useForgotPin ? pinData.current_password : null;
        await clientPortalService.changePin(currentPin, currentPassword, pinData.new_pin);
        setPinSuccess('PIN has been changed successfully');
      }
      setPinData({ current_pin: '', current_password: '', new_pin: '', confirm_pin: '' });
      setShowPinForm(false);
      setUseForgotPin(false);
    } catch (err) {
      setPinError(err.response?.data?.detail || 'Failed to update PIN');
    } finally {
      setPinLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center px-4">
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-green-500/30 p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Profile Updated!</h2>
          <p className="text-virgo-teal-300">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 mb-4">
            <User className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">
            {isFirstLogin ? 'Welcome to Your Client Portal' : 'Profile Settings'}
          </h1>
          <p className="text-virgo-teal-400 mt-2">
            {isFirstLogin
              ? 'Please verify and update your contact information to continue.'
              : 'Update your contact information below.'}
          </p>
        </div>

        {/* Back Link */}
        {!isFirstLogin && (
          <div className="mb-6">
            <button onClick={() => navigate('/portal/settings')} className="inline-flex items-center gap-2 text-virgo-teal-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Settings
            </button>
          </div>
        )}

        {/* Form */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-red-300">{error}</span>
              </div>
            )}

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <p className="text-amber-300 text-sm">
                Fields marked with <span className="text-red-400">*</span> are required before you can access the portal.
              </p>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  First Name <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="John"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Phone Number <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>

            {/* Company (Optional) */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Company <span className="text-virgo-teal-500 text-xs">(optional)</span>
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Company Name"
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Date of Birth <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* SSN collection removed from the client portal intake (Tier-0 F).
                Encryption machinery is retained server-side; no client-facing
                surface collects SSN. */}

            {/* Preferred Contact Method */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Preferred Contact Method
              </label>
              <div className="flex gap-3">
                {[
                  { value: 'phone', label: 'Phone' },
                  { value: 'email', label: 'Email' },
                  { value: 'text', label: 'Text' },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, preferred_contact_method: value }))}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      formData.preferred_contact_method === value
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                        : 'bg-virgo-navy-600 text-virgo-teal-400 border border-virgo-teal-700/50 hover:border-virgo-teal-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Street Address <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="123 Main Street"
                  required
                />
              </div>
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  City <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="City"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  State <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="TX"
                  maxLength={2}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  ZIP <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="12345"
                  required
                />
              </div>
            </div>


          {/* Security PIN - settings mode only */}
          {!isFirstLogin && (
            <div className="bg-virgo-navy-800/50 rounded-lg p-4 border border-virgo-teal-700/30">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-virgo-teal-400" />
                <h3 className="text-md font-semibold text-virgo-teal-100">Security PIN</h3>
              </div>
              <p className="text-sm text-virgo-teal-400 mb-3">
                Your 6-digit PIN is used for phone verification when calling the office.
              </p>

              {pinSuccess && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-2 mb-3 text-green-300 text-sm">
                  {pinSuccess}
                </div>
              )}

              {!showPinForm ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-virgo-teal-500" />
                    <span className={`text-sm ${hasPin ? 'text-green-400' : 'text-yellow-400'}`}>
                      {hasPin ? 'PIN is set' : 'No PIN set'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPinForm(true);
                      setPinMode(hasPin ? 'change' : 'set');
                      setPinError('');
                      setPinSuccess('');
                      setPinData({ current_pin: '', current_password: '', new_pin: '', confirm_pin: '' });
                    }}
                    className="px-3 py-1.5 bg-virgo-teal-600/30 text-virgo-teal-300 rounded-lg hover:bg-virgo-teal-600/50 transition text-sm"
                  >
                    {hasPin ? 'Change PIN' : 'Set PIN'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePinSubmit} className="space-y-3">
                  {pinError && (
                    <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-2 text-red-300 text-sm">
                      {pinError}
                    </div>
                  )}

                  {pinMode === 'change' && !useForgotPin && (
                    <div>
                      <label className="block text-xs font-medium text-virgo-teal-300 mb-1">Current PIN</label>
                      <input
                        type="password"
                        value={pinData.current_pin}
                        onChange={(e) => setPinData({...pinData, current_pin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                        maxLength={6}
                        inputMode="numeric"
                        placeholder="Current 6-digit PIN"
                        className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 text-sm"
                      />
                      <button type="button" onClick={() => setUseForgotPin(true)} className="text-cyan-400 text-xs mt-1 hover:underline">
                        Forgot PIN? Use password instead
                      </button>
                    </div>
                  )}

                  {pinMode === 'change' && useForgotPin && (
                    <div>
                      <label className="block text-xs font-medium text-virgo-teal-300 mb-1">Portal Password</label>
                      <input
                        type="password"
                        value={pinData.current_password}
                        onChange={(e) => setPinData({...pinData, current_password: e.target.value})}
                        placeholder="Your portal password"
                        className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 text-sm"
                      />
                      <button type="button" onClick={() => setUseForgotPin(false)} className="text-cyan-400 text-xs mt-1 hover:underline">
                        Use current PIN instead
                      </button>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-virgo-teal-300 mb-1">New PIN</label>
                    <input
                      type="password"
                      value={pinData.new_pin}
                      onChange={(e) => setPinData({...pinData, new_pin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      maxLength={6}
                      inputMode="numeric"
                      placeholder="6-digit PIN"
                      className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-virgo-teal-300 mb-1">Confirm New PIN</label>
                    <input
                      type="password"
                      value={pinData.confirm_pin}
                      onChange={(e) => setPinData({...pinData, confirm_pin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      maxLength={6}
                      inputMode="numeric"
                      placeholder="Confirm PIN"
                      className="w-full px-3 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 text-sm"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={pinLoading}
                      className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition text-sm disabled:opacity-50"
                    >
                      {pinLoading ? 'Saving...' : (pinMode === 'set' ? 'Set PIN' : 'Change PIN')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowPinForm(false); setPinError(''); setUseForgotPin(false); }}
                      className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 transition text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

            <div className="flex gap-3">
              {!isFirstLogin && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 py-4 bg-virgo-navy-600 text-virgo-teal-300 font-semibold rounded-lg hover:bg-virgo-navy-500 transition-all"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={saving || (isFirstLogin && !isFormValid())}
                className={`${isFirstLogin ? 'w-full' : 'flex-1'} py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/30 flex items-center justify-center gap-2`}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    {isFirstLogin ? 'Continue to Dashboard' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-virgo-teal-500 text-sm">
          <p>Your information is encrypted and stored securely.</p>
        </div>
      </div>
    </div>
  );
};

export default PortalProfileSetup;
